/**
 * Deterministic stacking order for assignment blocks that share an artist
 * slot. Shared by the schedule creator (CrewSchedule) and the read-only
 * timeline (Schedule page + client share).
 *
 * Rules:
 *  1. Filming members above assistant mirrors — the assistant always sits
 *     below whoever they follow.
 *  2. Within each group, alphabetical by crew name. Before this, ties kept
 *     the database fetch order, which is arbitrary — the same two crew could
 *     stack Georges-above-Kevin on one artist and Kevin-above-Georges on the
 *     next.
 *  3. Crew id as the final tie-break so two crew with identical names still
 *     stack the same way everywhere.
 */
export function sortStackedAssignments<T extends { crew_member_id: string }>(
  assignments: T[],
  isMirror: (a: T) => boolean,
  crewNameById: (crewId: string) => string,
): T[] {
  return [...assignments].sort((x, y) => {
    const mirrorDiff = Number(isMirror(x)) - Number(isMirror(y));
    if (mirrorDiff !== 0) return mirrorDiff;
    const nameDiff = crewNameById(x.crew_member_id).localeCompare(
      crewNameById(y.crew_member_id),
      undefined,
      { sensitivity: "base" },
    );
    if (nameDiff !== 0) return nameDiff;
    return x.crew_member_id.localeCompare(y.crew_member_id);
  });
}
